import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, Edit, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { ApiResponse, PaginationInfo, TipoAtendimentoListProps, TiposAtendimento } from "./interface/TipoAtendimento.interface";

const TipoAtendimentoList = ({ onEdit, onDelete, refreshTrigger }: TipoAtendimentoListProps) => {
  const [tiposAtendimento, setTiposAtendimento] = useState<TiposAtendimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();

  const fetchTiposAtendimento = async () => {
    try {
      setLoading(true);
      const response: { result: TiposAtendimento[] | ApiResponse } = await api.get(`/api/tipo_atendimento`);
      
      if (response.result && typeof response.result === 'object' && 'data' in response.result) {
        // Resposta com paginação
        const apiResponse = response.result as ApiResponse;
        setTiposAtendimento(apiResponse.data);
        setPagination(apiResponse.pagination);
        setCurrentPage(page);
      } else {
        // Resposta direta com array (sem paginação)
        setTiposAtendimento(Array.isArray(response.result) ? response.result : []);
        setPagination(null);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Erro ao carregar tipos de atendimento:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os tipos de atendimento",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiposAtendimento();
  }, [refreshTrigger]);

  // Filtro local por nome (backend não suporta busca)
  const filtered = tiposAtendimento.filter((t) => {
    if (!searchTerm.trim()) return true;
    const nome = t.nome?.toLowerCase?.() || "";
    const term = searchTerm.toLowerCase().trim();
    return nome.includes(term);
  });


  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground">Carregando tipos de atendimento...</div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Descrição</TableHead>
              {/* <TableHead>Cor</TableHead> */}
              <TableHead>Habilitado na Agenda</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  Nenhum tipo de atendimento encontrado
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((tipoAtendimento) => (
                <TableRow key={tipoAtendimento.id}>
                  <TableCell className="font-medium">{tipoAtendimento.nome}</TableCell>
                  <TableCell>{tipoAtendimento.categoria || '-'}</TableCell>
                  <TableCell className="max-w-xs truncate">
                    {tipoAtendimento.descricao || '-'}
                  </TableCell>
                  {/* <TableCell>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded border border-border"
                        style={{ backgroundColor: tipoAtendimento.cor || '#3b82f6' }}
                      />
                      <span className="text-sm text-muted-foreground">
                        {tipoAtendimento.cor || '#3b82f6'}
                      </span>
                    </div>
                  </TableCell> */}
                  <TableCell>
                    {tipoAtendimento.habilitado_agenda !== false ? (
                      <span className="text-green-600 font-medium">Sim</span>
                    ) : (
                      <span className="text-muted-foreground">Não</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => onEdit(tipoAtendimento)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => onDelete(tipoAtendimento.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Controles de Paginação */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t mt-4">
          <div className="text-sm text-muted-foreground">
            Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} tipos de atendimento
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchTiposAtendimento()}
              disabled={!pagination.hasPrev || loading}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {pagination.page} de {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchTiposAtendimento()}
              disabled={!pagination.hasNext || loading}
            >
              Próxima
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default TipoAtendimentoList;
